/* eslint-disable no-use-before-define */
/* eslint-disable consistent-return */
/* eslint-disable react/sort-comp */
import React, { useRef, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { animateScroll } from 'react-scroll';

import Link from '@components/i18n/Link';
import ButtonSecondaryBackground from './ButtonSecondaryBackground';
import { resizeModule } from '@shcc/modules/ResizeModule';
import {
  THEME_DARK,
  COLOR_BLACK,
  COLOR_WHITE,
  THEME_LIGHT,
} from '@shcc/constants';
import { UserStoreContext } from '@common/stores';
import styles from './ButtonSecondary.module.scss';

export const ButtonSecondary = ({
  className,
  isDark,
  isSmall,
  theme,
  label,
  link,
  style,
}) => {
  const { motionDisabled } = useContext(UserStoreContext);
  const element = useRef(null);
  const background = useRef(null);
  const dimensions = useRef({
    width: 0,
    height: 0,
  });

  const handleLink = e => {
    // if internal link, allow i18n/Link to handle
    if (/^\/(?!\/)/.test(link.href)) {
      return true;
    }

    e.preventDefault();
    const destination = link.href;

    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      if (destination) {
        // external link
        if (destination.startsWith('http')) {
          window.location.href = destination;

          // #anchor link (animate scroll)
        } else if (destination.indexOf('#') !== -1) {
          const anchor = destination.substring(destination.indexOf('#') + 1); // newsletter
          const anchorLength = anchor.length > 0;
          if (anchorLength) {
            const anchorPos = document
              .getElementById(anchor)
              .getBoundingClientRect().top;
            const scrollCorrect = window.scrollY;
            const margin = 200;
            const scrollDistance = anchorPos + scrollCorrect - margin;
            animateScroll.scrollTo(scrollDistance, {
              duration: 1700,
              smooth: 'easeOutCubic',
              offset: 0,
            });
          }
          return false;

          // file link
        } else if (/\.[0-9a-z]+$/i.test(destination)) {
          window.location.href = destination;
        }
      }
    }
  };

  const setDimensions = () => {
    dimensions.current.width = element.current.clientWidth;
    dimensions.current.height = element.current.clientHeight;
  };

  const setBackgroundSize = () => {
    if (motionDisabled) return;
    const scale = window.devicePixelRatio > 1 ? 2 : 1;

    background.current.setSize(
      dimensions.current.width,
      dimensions.current.height,
      scale
    );
  };

  const setupEventListeners = () => {
    if (!motionDisabled) {
      element.current.addEventListener('mouseenter', mouseEnterHandler);
      element.current.addEventListener('mouseleave', mouseLeaveHandler);
    }

    resizeModule.addEventListener('resize', resizeHandler);
    resizeModule.addEventListener('resize:complete', resizeHandler);
  };

  const removeEventListeners = () => {
    if (!motionDisabled) {
      element.current.removeEventListener('mouseenter', mouseEnterHandler);
      element.current.removeEventListener('mouseleave', mouseLeaveHandler);
    }

    resizeModule.removeEventListener('resize', resizeHandler);
    resizeModule.removeEventListener('resize:complete', resizeHandler);
  };

  /**
   *
   * Handlers
   */
  const mouseEnterHandler = () => {
    const color = theme === THEME_DARK ? COLOR_BLACK : COLOR_WHITE;
    background.current.add(color);
  };

  const mouseLeaveHandler = () => {
    background.current.remove();
  };

  const resizeHandler = () => {
    setDimensions();
    setBackgroundSize();
  };

  useEffect(() => {
    setDimensions();
    setupEventListeners();
    setBackgroundSize();
    return () => {
      removeEventListeners();
    };
  }, [motionDisabled]);

  return (
    <div>
      <Link
        to={link.href}
        target={link.target}
        className={classNames(
          styles.button,
          { [styles.buttonDark]: isDark },
          { [styles.buttonSmall]: isSmall },
          className,
          theme === THEME_DARK ? styles.buttonThemeDark : null
        )}
        style={style}
        ref={element}
        aria-label={label}
        onClick={handleLink}
      >
        {!motionDisabled && <ButtonSecondaryBackground ref={background} />}
        <span className={styles.label}>{label}</span>
      </Link>
    </div>
  );
};

ButtonSecondary.defaultProps = {
  className: '',
  isDark: false,
  isSmall: false,
  theme: THEME_LIGHT,
  style: {},
};

ButtonSecondary.propTypes = {
  label: PropTypes.string.isRequired,
  link: PropTypes.shape({
    withLanguage: PropTypes.bool,
    target: PropTypes.string,
    href: PropTypes.string,
  }).isRequired,
  className: PropTypes.string,
  isDark: PropTypes.bool,
  isSmall: PropTypes.bool,
  theme: PropTypes.string,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default ButtonSecondary;
